import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw, Timer, TimerOff, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatTime } from '@/lib/dateUtils'

/**
 * How often a live screen re-reads its data, chosen by the person watching it.
 *
 * ONE FORMAT, PER-SCREEN INTERVALS. Three hand-rolled versions of this had grown — the recovery
 * Gantt's, Ingress Monitoring's own, and the recovery map's Select — each with a different look,
 * a different set of intervals and different ideas about what "Off" looks like. They are all this
 * component now.
 *
 * The intervals are a prop because they genuinely differ: a live recovery is worth re-reading
 * every ten seconds, a map of sites and travel times is not, and forcing one list on both would
 * either make the map wasteful or make the Gantt sluggish. What must not differ is the control.
 *
 * The choice PERSISTS. An interval is a preference about how closely you are watching, not a
 * property of one visit, and re-picking it on every navigation is the kind of small friction that
 * makes people leave it on the default and stop trusting the number.
 */

export type RefreshInterval = number | false

export const REFRESH_INTERVALS: { labelKey: string; fallback: string; value: RefreshInterval }[] = [
  { labelKey: 'refresh.every10s', fallback: '10s', value: 10_000 },
  { labelKey: 'refresh.every30s', fallback: '30s', value: 30_000 },
  { labelKey: 'refresh.every1m', fallback: '1m', value: 60_000 },
  { labelKey: 'refresh.every5m', fallback: '5m', value: 300_000 },
  { labelKey: 'refresh.off', fallback: 'Off', value: false },
]

/** The longer set, for screens where a ten-second poll would be waste rather than attentiveness. */
export const SLOW_REFRESH_INTERVALS: typeof REFRESH_INTERVALS = [
  { labelKey: 'refresh.every1m', fallback: '1m', value: 60_000 },
  { labelKey: 'refresh.every2m', fallback: '2m', value: 120_000 },
  { labelKey: 'refresh.every5m', fallback: '5m', value: 300_000 },
  { labelKey: 'refresh.every10m', fallback: '10m', value: 600_000 },
  { labelKey: 'refresh.off', fallback: 'Off', value: false },
]

/** Reads the stored preference once, falling back when nothing is stored or storage is blocked. */
export function useRefreshInterval(
  storageKey: string,
  fallback: RefreshInterval = 30_000,
  intervals: typeof REFRESH_INTERVALS = REFRESH_INTERVALS,
) {
  const [interval, setIntervalValue] = useState<RefreshInterval>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw === null) return fallback
      if (raw === 'false') return false
      const n = Number(raw)
      // Only values the control actually offers — a stale or hand-edited key must not be able to
      // set a one-second poll against a live recovery.
      return intervals.some((o) => o.value === n) ? n : fallback
    } catch {
      return fallback
    }
  })
  const set = (v: RefreshInterval) => {
    setIntervalValue(v)
    try { localStorage.setItem(storageKey, String(v)) } catch { /* private mode; the session still works */ }
  }
  return [interval, set] as const
}

export function AutoRefreshControl({
  value, onChange, onRefresh, isRefreshing, updatedAt, className,
  intervals = REFRESH_INTERVALS,
}: {
  value: RefreshInterval
  onChange: (v: RefreshInterval) => void
  onRefresh: () => void
  isRefreshing?: boolean
  /** When the data on screen was last read. Without it "auto" is a claim you cannot check. */
  updatedAt?: number | null
  className?: string
  /** Defaults to the live set. Pass SLOW_REFRESH_INTERVALS for screens that poll expensively. */
  intervals?: typeof REFRESH_INTERVALS
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [open])

  const current = intervals.find((o) => o.value === value)
  const currentLabel = current ? t(current.labelKey, current.fallback) : String(value)

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {updatedAt ? (
        <span className="text-[11px] text-muted-foreground">
          {t('refresh.updated', 'Updated {{time}}', { time: formatTime(updatedAt) })}
        </span>
      ) : null}

      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={onRefresh} disabled={isRefreshing}>
        <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
        {t('refresh.now', 'Refresh')}
      </Button>

      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          className={cn('h-7 px-2 text-xs', value !== false && 'border-green-300 dark:border-green-800')}
          onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {value !== false
            ? <Timer className="mr-1.5 h-3.5 w-3.5 text-green-600" />
            : <TimerOff className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />}
          {t('refresh.auto', 'Auto')}: {currentLabel}
        </Button>

        {open && (
          <div
            className="absolute right-0 top-full z-50 mt-1 min-w-[150px] rounded-lg border bg-popover py-1 shadow-md"
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {t('refresh.autoRefresh', 'Auto refresh')}
            </div>
            {intervals.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                role="menuitemradio"
                aria-checked={value === opt.value}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-1.5 text-sm transition-colors hover:bg-accent',
                  value === opt.value && 'font-medium text-primary',
                )}
                onClick={() => { onChange(opt.value); setOpen(false) }}
              >
                <span className="flex items-center gap-2">
                  {opt.value === false
                    ? <TimerOff className="h-3.5 w-3.5 text-muted-foreground" />
                    : <Timer className="h-3.5 w-3.5 text-green-600" />}
                  {opt.value === false
                    ? t('refresh.off', 'Off')
                    : t('refresh.everyN', 'Every {{n}}', { n: t(opt.labelKey, opt.fallback) })}
                </span>
                {value === opt.value && <Check className="h-3.5 w-3.5 text-primary" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
